import * as React from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  DatePicker,
  FlexRow,
  Form,
  H1,
  Label,
  Select,
  Table,
  Text,
} from "atoms";
import { Head } from "features/misc";
import { useAdminReport } from "../api/useAdminReports";
import { AdminReportType } from "../types";

const oneWeekAgo = () =>
  new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

const reportHeadings = (report: AdminReportType) => {
  if (report === "curatorEarnings") {
    return ["User", "HUG Points Earned"];
  }
  if (report === "curatorReviews") {
    return ["User", "Reviews Created"];
  }
  return ["User", "Review Votes"];
};

const reportTitle = (report: AdminReportType) => {
  if (report === "curatorEarnings") {
    return "HUG Points Earned";
  }
  if (report === "curatorReviews") {
    return "New Reviews Created";
  }
  return "New Review Votes";
};

type DateFormFields = {
  startDate: string;
  endDate: string;
};

const initialStartDate = oneWeekAgo();
const initialEndDate = new Date().toISOString().split("T")[0];

function CuratorReport() {
  const [startDate, setStartDate] = React.useState<string>(initialStartDate);
  const [endDate, setEndDate] = React.useState<string>(initialEndDate);
  const [report, setReport] =
    React.useState<AdminReportType>("curatorEarnings");
  const headings = reportHeadings(report);
  const { data, isLoading } = useAdminReport(report, startDate, endDate);

  const { handleSubmit, register } = useForm<DateFormFields>({
    defaultValues: {
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
  });

  const onSubmit = handleSubmit((formData: DateFormFields) => {
    const { startDate: newStartDate, endDate: newEndDate } = formData;
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  });

  const showReport = data?.data && !isLoading;

  return (
    <main>
      <Head title="Curator Reports | Admin" />

      <Form onSubmit={onSubmit}>
        <FlexRow alignItems="flex-end">
          <Label>
            Select Report
            <Select
              onChange={(e) => setReport(e.target.value as AdminReportType)}
            >
              <option value="curatorEarnings">Points Earned</option>
              <option value="curatorReviews">New Reviews</option>
              <option value="curatorVotes">New Review Votes</option>
            </Select>
          </Label>
          <Label>
            Start Date
            <DatePicker {...register("startDate", { valueAsDate: true })} />
          </Label>
          <Label>
            End Date
            <DatePicker {...register("endDate", { valueAsDate: true })} />
          </Label>
          <Button type="submit">Update</Button>
        </FlexRow>
      </Form>

      <H1>Curator Report: {reportTitle(report)}</H1>

      {showReport ? (
        <Table>
          <thead>
            <tr>
              <th>{headings[0]}</th>
              <th>{headings[1]}</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((row: string[]) => (
              <tr key={row[0]}>
                <td>{row[0]}</td>
                <td>{row[1]}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Text>
          <em>Loading...</em>
        </Text>
      )}
    </main>
  );
}

export { CuratorReport };
